/*
 * WR-024: Horizon entries save independently from the task. Numeric edits
 * preview through the shared scheduler and commit when the field loses focus.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { CalendarRange } from 'lucide-react';
import { Link } from 'react-router-dom';
import { placementsByTask, recompute, reasonFor, scheduleMismatches } from './horizonPlan';
import { formatWindow } from './horizonTime';
import './HorizonTaskSection.css';

const messageOf = e => e.response?.data?.error || e.message;
const entryFor = (h, taskId) => h?.entries?.find(e => String(e.task) === String(taskId));
const labelOf = row => row?.name || row?.snapshot?.bstId?.replace(/^BST-/, '') || row?.snapshot?.title || 'Removed task';

function HorizonTaskSection({ task, project, focus, initialHorizonId, onScheduleChange }) {
  const [horizonList, setHorizonList] = useState([]);
  const [shownHorizon, setShownHorizon] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);
  const generation = useRef(null);

  const show = h => {
    const mismatches = scheduleMismatches(h);
    if (mismatches.length) console.error('Horizon schedule mismatch (client vs server)', mismatches);
    setShownHorizon(h);
    setDraft({});
    setError(null);
    // Keep the complete project list so a removed placement can be added again.
    setHorizonList(list => list.map(item => String(item._id) === String(h._id) ? h : item));
  };

  useEffect(() => {
    const request = {};
    generation.current = request;
    setShownHorizon(null);
    setDraft({});
    setError(null);
    setBusy(false);
    (async () => {
      try {
        const { data: list } = await axios.get(`/horizons/project/${project._id}`);
        if (request !== generation.current) return;
        setHorizonList(list);
        const placements = placementsByTask(list).get(task._id) || [];
        const placement = placements.find(p => p.horizonId === initialHorizonId) || placements[0];
        if (placement) {
          const { data } = await axios.get(`/horizons/${placement.horizonId}`);
          if (request === generation.current) show(data);
        }
      } catch (e) {
        if (request === generation.current) setError(messageOf(e));
      }
    })();
    return () => { generation.current = null; };
  }, [task._id, project._id, initialHorizonId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (focus) rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [focus, shownHorizon]);

  const horizonId = shownHorizon ? String(shownHorizon._id) : '';
  const savedEntry = entryFor(shownHorizon, task._id);
  const currentView = useMemo(() => {
    if (!entryFor(shownHorizon, task._id) || !Object.keys(draft).length) return shownHorizon;
    const copy = JSON.parse(JSON.stringify(shownHorizon));
    Object.assign(entryFor(copy, task._id), draft);
    return recompute(copy);
  }, [shownHorizon, draft, task._id]);
  const entry = entryFor(currentView, task._id);

  useEffect(() => { onScheduleChange?.(currentView); }, [currentView, onScheduleChange]);

  const mutate = async action => {
    if (busy) return;
    const request = generation.current;
    setBusy(true);
    setError(null);
    try {
      const { data } = await action();
      if (request === generation.current) show(data);
    } catch (e) {
      if (request === generation.current) {
        setError(messageOf(e));
        setDraft({});
      }
    } finally {
      if (request === generation.current) setBusy(false);
    }
  };

  const handleHorizonChange = async e => {
    if (busy) return;
    const value = e.target.value;
    if (!value) {
      if (!savedEntry || !window.confirm(`Remove this task from ${shownHorizon.name}?`)) return;
      await mutate(() => axios.delete(`/horizons/${horizonId}/entries/${savedEntry._id}`));
      return;
    }
    const existing = horizonList.find(h => String(h._id) === value)?.entries?.some(e => String(e.task) === String(task._id));
    await mutate(() => existing
      ? axios.get(`/horizons/${value}`)
      : axios.post(`/horizons/${value}/entries`, [{ task: task._id }]));
  };

  const update = patch => savedEntry && mutate(() => axios.put(
    `/horizons/${horizonId}/entries/${savedEntry._id}`, patch,
  ));
  const handleFieldChange = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));
  const handleFieldBlur = field => {
    if (draft[field] !== undefined) update({ [field]: draft[field] });
  };
  const handleLaneChange = e => {
    // Preserve additional lanes when editing the first one; "No lane" clears all.
    const first = e.target.value;
    update({ lanes: first ? [first, ...(savedEntry.lanes || []).slice(1).filter(id => id !== first)] : [] });
  };
  const handleAfterRemove = id => update({ after: entry.after.filter(a => String(a) !== String(id)) });
  const handleAfterAdd = e => {
    const id = e.target.value;
    if (id) update({ after: [...entry.after, id] });
    e.target.value = '';
  };
  const handlePin = () => {
    if (!entry) return;
    if (entry.pinnedStart != null) { update({ pinnedStart: null }); return; }
    const answer = window.prompt('Pin the start to week:', String(entry.start));
    if (answer == null || !answer.trim()) return;
    const pinnedStart = Number(answer);
    if (!Number.isInteger(pinnedStart) || pinnedStart < 1) {
      setError('Pinned start must be a whole week of 1 or later.');
      return;
    }
    update({ pinnedStart });
  };

  const getAvailableAfterOptions = () => {
    if (!entry) return [];
    return [...currentView.entries, ...currentView.milestones]
      .filter(row => row.order < entry.order && !entry.after.includes(String(row._id)))
      .sort((a, b) => a.order - b.order)
      .map(row => ({ id: String(row._id), label: row.name || row.snapshot?.bstId || row.snapshot?.title || 'Removed task' }));
  };

   // Get lanes
  const getLanes = () => {
    if (!shownHorizon) return [];
    return shownHorizon.lanes || [];
  };

  const lanes = getLanes();

  return (
    <div className="hzs" ref={rootRef}>
      <div className="sidebar-section">
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
          <CalendarRange size={16} />
          Horizon
        </label>

        <select
          className="hzs-horizon"
          value={savedEntry ? horizonId : ''}
          disabled={busy}
          onChange={handleHorizonChange}
          style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '14px' }}
        >
          <option value="">— Not on a horizon —</option>
          {horizonList.map(h => (
            <option key={h._id} value={h._id}>{h.name}</option>
          ))}
        </select>

        {error && <div className="hzs-error" role="alert">{error}</div>}

        {shownHorizon && entry && (
          <div className="hzs-fields" style={{ marginTop: '16px' }}>
            <fieldset disabled={busy} className="hzs-editors" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {/* Lane selector */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: 'var(--bastion-text-dim)', marginBottom: '8px', display: 'block' }}>
                  Lane
                </label>
                <select
                  className="hzs-lane"
                  value={entry.lanes && entry.lanes.length > 0 ? entry.lanes[0] : ''}
                  onChange={handleLaneChange}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '14px' }}
                >
                  <option value="">No lane</option>
                  {lanes.map(l => (
                    <option key={l._id} value={l._id}>{l.name}</option>
                  ))}
                </select>
              </div>

              {/* Duration */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: 'var(--bastion-text-dim)', marginBottom: '8px', display: 'block' }}>
                  Duration
                </label>
                <input
                  className="hzs-duration"
                  type="number"
                  min="1"
                  value={draft.duration !== undefined ? draft.duration : (entry.duration ?? '')}
                  onChange={(e) => handleFieldChange('duration', Number(e.target.value))}
                  onBlur={() => handleFieldBlur('duration')}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '14px' }}
                />
              </div>

              {/* After dependencies - chips and add selector */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: 'var(--bastion-text-dim)', marginBottom: '8px', display: 'block' }}>
                  After
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {entry.after && entry.after.length > 0 ? entry.after.map(afterId => {
                    const afterEntry = [...currentView.entries, ...currentView.milestones].find(e => String(e._id) === String(afterId));
                    const label = labelOf(afterEntry);
                    return (
                      <span key={afterId} className="hzs-after-chip" data-id={afterId} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '11px', fontFamily: 'var(--font-mono)' }}>
                        <span className="hzs-after-label">{label}</span>
                        <button className="hzs-after-remove" onClick={() => handleAfterRemove(afterId)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', color: 'var(--bastion-text-dim)' }}>×</button>
                      </span>
                    );
                  }) : <span style={{ fontSize: '12px', color: 'var(--bastion-text-faint)' }}>No dependencies</span>}
                </div>
                <select
                  className="hzs-after-add"
                  onChange={handleAfterAdd}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '14px' }}
                >
                  <option value="">+ Add…</option>
                  {getAvailableAfterOptions().map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Not before */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: 'var(--bastion-text-dim)', marginBottom: '8px', display: 'block' }}>
                  Not before
                </label>
                <input
                  className="hzs-notbefore"
                  type="number"
                  min="1"
                  value={draft.notBefore !== undefined ? (draft.notBefore === null ? '' : draft.notBefore) : (entry.notBefore == null ? '' : entry.notBefore)}
                  onChange={(e) => handleFieldChange('notBefore', e.target.value === '' ? null : Number(e.target.value))}
                  onBlur={() => handleFieldBlur('notBefore')}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '14px' }}
                />
              </div>

              {/* Window display */}
              <div style={{ gridColumn: 'span 2' }}>
                <div style={{ background: 'var(--bastion-bg-inset)', border: '1px solid var(--bastion-border)', borderRadius: 'var(--radius-md)', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <span className="hzs-mode" style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', textTransform: 'uppercase', color: 'var(--bastion-text-dim)' }}>
                        {entry.pinnedStart != null ? 'Scheduled · Pinned' : 'Scheduled · Auto'}
                      </span>
                      <span className="hzs-weeks" style={{ fontFamily: 'var(--font-mono)', fontSize: '13px', color: 'var(--bastion-text)' }}>
                        {formatWindow(shownHorizon.anchorDate, entry.start, entry.end)}
                      </span>
                      <span className="hzs-reason" style={{ fontSize: '12px', color: 'var(--bastion-text-dim)' }}>
                        {reasonFor(currentView, entry._id)}
                      </span>
                    </div>
                    <Link
                      className="hzs-open"
                      to={`/project/${project._id}/horizon/${horizonId}?row=${entry._id}`}
                      style={{ color: 'var(--bastion-steel-text)', fontSize: '14px', textDecoration: 'none' }}
                    >
                      Open in Horizon
                    </Link>
                  </div>
                </div>
              </div>

              {/* Pin button */}
              <div style={{ gridColumn: 'span 2' }}>
                <button
                  className="hzs-pin"
                  onClick={handlePin}
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '14px', color: 'var(--text-primary)' }}
                >
                  {entry.pinnedStart != null ? `Unpin (W${entry.pinnedStart})` : 'Pin start…'}
                </button>
              </div>
            </fieldset>
          </div>
        )}
      </div>
    </div>
  );
}

export default HorizonTaskSection;
